// implements: CRS-V21-RT-001 (Sidebar SSE Manager)
import type { QueryClient } from '@tanstack/react-query';
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
import { useSidebarStore } from '@/features/sidebar-store';
import { useAuthStore } from '@/shared/stores/authStore';
import { toast } from 'sonner';

export class SidebarSSEManager {
  private abortController: AbortController | null = null;
  private bc: BroadcastChannel | null = null;
  private qc: QueryClient;
  public isLeader = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;

  constructor(queryClient: QueryClient) {
    this.qc = queryClient;
    try {
      this.bc = new BroadcastChannel('sidebar-sse-v21');
      this.bc.onmessage = (e) => {
        if (e.data?.type === 'sse-event') {
          this.handleEvent(e.data.eventName, e.data.payload, false);
        }
      };
    } catch {
      // BroadcastChannel unavailable in some test/legacy environments
    }
    this.electLeader();
  }

  private async electLeader() {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      try {
        await navigator.locks.request('sidebar-sse-leader', async () => {
          this.isLeader = true;
          this.connect();
          await new Promise<void>(() => {}); // Hold lock until tab closes
        });
      } catch {
        this.isLeader = true;
        this.connect();
      }
    } else {
      this.isLeader = true;
      this.connect();
    }
  }

  public connect() {
    if (typeof window === 'undefined') return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    let lastId = '';
    try {
      lastId = sessionStorage.getItem('sidebar-sse-last-event-id') ?? '';
    } catch {
      // Ignore sessionStorage issues
    }

    const url = `/api/v1/sidebar/stream${lastId ? `?lastEventId=${encodeURIComponent(lastId)}` : ''}`;
    const token = useAuthStore.getState().token ?? '';

    fetchEventSource(url, {
      signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: EventStreamContentType,
        'Cache-Control': 'no-cache',
      },
      onopen: async (response) => {
        if (response.status === 401) {
          useSidebarStore.getState().setSseConnected(false);
          useAuthStore.getState().logout?.();
          return;
        }
        if (!response.ok) {
          throw new Error(`SSE open failed: HTTP ${response.status}`);
        }
        useSidebarStore.getState().setSseConnected(true);
        this.reconnectAttempt = 0;
      },
      onmessage: (ev) => {
        // Handle keep-alive comments (starts with :)
        if (ev.data && ev.data.startsWith(':')) return;

        if (ev.id) {
          try {
            sessionStorage.setItem('sidebar-sse-last-event-id', ev.id);
          } catch {
            // Ignore
          }
        }

        try {
          const parsed = JSON.parse(ev.data);
          const event = ev.event || parsed.event || parsed.type || 'message';
          const data = parsed.data || parsed.payload || parsed;
          this.handleEvent(event, data, true);
          this.reconnectAttempt = 0;
        } catch {
          // Ignore unparseable frames
        }
      },
      onerror: (err) => {
        useSidebarStore.getState().setSseConnected(false);
        this.reconnectAttempt++;
        if (this.reconnectAttempt >= 5) {
          throw err; // Stop retrying after 5 attempts
        }
        return Math.min(1000 * Math.pow(2, this.reconnectAttempt), 30000);
      },
      openWhenHidden: false,
    }).catch((err) => {
      useSidebarStore.getState().setSseConnected(false);
      if (import.meta.env.DEV) {
        console.warn('[SidebarSSE] Connection terminated:', err);
      }
    });
  }

  public handleEvent(event: string, data: unknown, broadcast: boolean) {
    if (broadcast && this.isLeader && this.bc) {
      try {
        this.bc.postMessage({ type: 'sse-event', eventName: event, payload: data });
      } catch {
        // Ignore broadcast failure
      }
    }

    const d = (data as Record<string, unknown>) || {};
    switch (event) {
      case 'war_room:created':
        this.qc.invalidateQueries({ queryKey: ['war-room'] });
        useSidebarStore.getState().setContextBadge('team', {
          count: 1,
          type: 'dot',
          color: 'error',
          pulsing: true,
        });
        break;

      case 'war_room:resolved':
        this.qc.invalidateQueries({ queryKey: ['war-room'] });
        this.qc.invalidateQueries({ queryKey: ['ctx-badge', 'team'] });
        useSidebarStore.getState().clearContextBadge('team');
        break;

      case 'badge:updated':
        this.qc.invalidateQueries({ queryKey: ['ctx-badge'] });
        break;

      case 'plugin:status_changed':
        this.qc.invalidateQueries({ queryKey: ['installed-plugins'] });
        break;

      case 'plugin:nav_item_updated':
        this.qc.invalidateQueries({ queryKey: ['installed-plugins'] });
        break;

      case 'job:status_changed':
        this.qc.invalidateQueries({ queryKey: ['scheduled-summary'] });
        if (d.status === 'failed') {
          toast.error(`🔴 Job failed: ${d.jobName ?? 'Scheduled Job'}`, {
            action: {
              label: 'View logs',
              onClick: () => {
                if (d.jobId && typeof window !== 'undefined') {
                  window.open(`/scheduler/${d.jobId}/logs`, '_self');
                }
              },
            },
            duration: 10000,
          });
        }
        break;

      case 'permission:changed':
        this.qc.invalidateQueries({ queryKey: ['user-permissions'] });
        this.qc.invalidateQueries({ queryKey: ['role-nav-config'] });
        break;

      case 'announcement:published':
        this.qc.invalidateQueries({ queryKey: ['announcements'] });
        this.qc.invalidateQueries({ queryKey: ['ctx-badge', 'company'] });
        break;
    }
  }

  public disconnect() {
    this.abortController?.abort();
    this.abortController = null;
    this.bc?.close();
    this.bc = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    useSidebarStore.getState().setSseConnected(false);
  }
}

let manager: SidebarSSEManager | null = null;

export function initSidebarSSE(queryClient: QueryClient): SidebarSSEManager {
  manager?.disconnect();
  manager = new SidebarSSEManager(queryClient);
  return manager;
}

export function disconnectSidebarSSE() {
  manager?.disconnect();
  manager = null;
}

export function getSidebarSSEManager(): SidebarSSEManager | null {
  return manager;
}
